import React, { useEffect, useRef, useState } from 'react';
import '../styles/fileDropZone.css';

const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');
let activePasteZone = null;

const clipboardFiles = (clipboard) => {
    const files = Array.from(clipboard?.files || []);
    // Screenshots are often exposed as items but not in clipboardData.files.
    const picked = files.length ? files : Array.from(clipboard?.items || [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter(Boolean);

    return picked.map((file) => {
        if (/\.[a-z0-9]+$/i.test(file.name)) return file;
        const imageType = file.type.match(/^image\/(jpeg|png|webp|gif|heic)$/i)?.[1];
        return imageType
            ? new File([file], `pasted-image.${imageType === 'jpeg' ? 'jpg' : imageType}`, { type: file.type })
            : file;
    });
};

const isEditable = (target) => target instanceof Element && !!target.closest('input, textarea, select, [contenteditable="true"]');

/** A drop target around an existing panel. Text drags and ordinary clicks pass through. */
const FileDropZone = ({ as: Element = 'div', className = '', disabled = false, autoSelectPaste = false, onFiles, children, label = 'Drop files to attach' }) => {
    const [active, setActive] = useState(false);
    const zoneRef = useRef(null);
    const latest = useRef({ disabled, onFiles });
    latest.current = { disabled, onFiles };

    const pasteFiles = (event) => {
        if (event.defaultPrevented) return;
        const files = clipboardFiles(event.clipboardData);
        if (!files.length || latest.current.disabled) return;
        event.preventDefault();
        activePasteZone = zoneRef.current;
        latest.current.onFiles?.(files);
    };

    useEffect(() => {
        const zone = zoneRef.current;
        const clear = () => setActive(false);
        if (autoSelectPaste && !activePasteZone) activePasteZone = zone;
        const pasteOutside = (event) => {
            if (activePasteZone !== zone || zone.contains(event.target) || isEditable(event.target)) return;
            pasteFiles(event);
        };
        window.addEventListener('drop', clear);
        window.addEventListener('dragend', clear);
        document.addEventListener('paste', pasteOutside);
        return () => {
            if (activePasteZone === zone) activePasteZone = null;
            window.removeEventListener('drop', clear);
            window.removeEventListener('dragend', clear);
            document.removeEventListener('paste', pasteOutside);
        };
    }, [autoSelectPaste]);

    return (
        <Element
            ref={zoneRef}
            className={`${className} file-drop-zone`}
            onPointerEnter={() => { activePasteZone = zoneRef.current; }}
            onPointerDown={() => { activePasteZone = zoneRef.current; }}
            onFocusCapture={() => { activePasteZone = zoneRef.current; }}
            onPaste={pasteFiles}
            onDragEnter={(event) => {
                if (disabled || !hasFiles(event)) return;
                event.preventDefault();
                setActive(true);
            }}
            onDragOver={(event) => {
                if (!hasFiles(event)) return;
                event.preventDefault();
                if (disabled) return;
                event.dataTransfer.dropEffect = 'copy';
                if (!active) setActive(true);
            }}
            onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setActive(false);
            }}
            onDrop={(event) => {
                setActive(false);
                if (!hasFiles(event)) return;
                event.preventDefault();
                event.stopPropagation();
                if (disabled) return;
                const files = Array.from(event.dataTransfer.files || []);
                if (files.length) onFiles?.(files);
            }}
        >
            {children}
            {active && !disabled && (
                <div className="file-drop-overlay" role="status" aria-live="polite">
                    <div className="file-drop-outline">
                        <span className="file-drop-symbol" aria-hidden="true">＋</span>
                        <strong>{label}</strong>
                        <span>Release to add to this {Element === 'section' ? 'task' : 'conversation'}</span>
                    </div>
                </div>
            )}
        </Element>
    );
};

export default FileDropZone;
